// Explore's state in the address: what the page shows is read from the query
// string and written back to it, so that a copied address shows the same
// list. The module is pure: it knows nothing of the router or of React.
//
// The keys are Explore's own. The helper of the existing search is not
// extended: its keys belong to that page.
//
// The address format of the filters. It is a contract: other views build
// links into Explore with it.
//
// - A pick is one parameter named like its filter, with the value the
//   filter lists (`FilterValue.value`: an id or a fixed code). Several picks
//   of one filter repeat the parameter. The names are those of the search
//   request: category, capability, condition, mode, level, status, maker,
//   maker_country, operator, operator_country, programme. A country
//   filter takes a country's ISO code or a region's id (`maker_country=NATO`).
//
//     /explore?capability=backbone:capability_part:nav.determine-position
//             &condition=backbone:capability_part:cond.gnss-denied
//
//   (`encodeExploreState` escapes the colons as %3A; both forms are read.)
//
// - A numeric range is one parameter `numeric` of the form
//   `key:minimum:maximum`, either bound empty: `numeric=weight:2:10`,
//   `numeric=weight::10`, `numeric=weight:2:`. One range per key. The bounds
//   are in the unit the filter states for the key.
//
// Both are what the search request takes, so the request is built from the
// state without a table in between (see `toProductSearchParams`).

import {
  ProductFilterPicks,
  ProductSearchParams,
  ProductSelectionParams,
} from "../../services/productPlaneApi";
import {
  ProductFilterName,
  ProductFlatFilterName,
  ProductSort,
} from "../../types/productPlane";

// How the results are shown
export type ExploreViewMode = "cards" | "table";

// A range of one numeric specification, bounds included
export interface ExploreRange {
  // The specification, as the filter keys it
  key: string;
  // Null for no lower bound
  minimum: number | null;
  // Null for no upper bound. With neither bound: every result that states
  // the specification.
  maximum: number | null;
}

// The picks of every filter: the values picked, in the order picked
export type ExplorePicks = { [Name in ProductFilterName]: string[] };

export interface ExploreState extends ExplorePicks {
  // The submitted keyword, trimmed; empty for none
  keyword: string;
  sort: ProductSort;
  // The page, counted from 1
  page: number;
  view: ExploreViewMode;
  // Whether product mentions that carry no name are listed
  unnamed: boolean;
  // The numeric ranges, one per key at most
  numeric: ExploreRange[];
}

// Results per page
export const EXPLORE_PAGE_SIZE = 50;

export const EXPLORE_SORTS: readonly ProductSort[] = [
  "relevance",
  "sources",
  "latest",
];

export const EXPLORE_VIEW_MODES: readonly ExploreViewMode[] = [
  "cards",
  "table",
];

// The filters with values to pick, in the order of the panel. Each is a part
// of the state, a parameter of the address and a field of the request, all
// of the same name.
export const EXPLORE_FILTER_NAMES: readonly ProductFilterName[] = [
  "category",
  "capability",
  "mission",
  "condition",
  "mode",
  "level",
  "status",
  "maker",
  "maker_country",
  "operator",
  "operator_country",
  "programme",
];

// The state of an address without parameters. A part equal to its default
// is left out of the address.
export const DEFAULT_EXPLORE_STATE: ExploreState = {
  keyword: "",
  sort: "relevance",
  page: 1,
  view: "cards",
  unnamed: false,
  category: [],
  capability: [],
  mission: [],
  condition: [],
  mode: [],
  level: [],
  status: [],
  maker: [],
  maker_country: [],
  operator: [],
  operator_country: [],
  programme: [],
  numeric: [],
};

// The parts a filter panel reads and changes: the picks and the ranges. The
// analytics page holds them too, so its scope is narrowed by the same panel.
export type ExploreFilterState = ExplorePicks & Pick<ExploreState, "numeric">;

// The change that removes every pick and every range, and nothing else
export const NO_EXPLORE_FILTERS: ExploreFilterState = {
  category: [],
  capability: [],
  mission: [],
  condition: [],
  mode: [],
  level: [],
  status: [],
  maker: [],
  maker_country: [],
  operator: [],
  operator_country: [],
  programme: [],
  numeric: [],
};

// The query parameter of each part of the state
export const EXPLORE_PARAMS: { [Part in keyof ExploreState]: string } = {
  keyword: "q",
  sort: "sort",
  page: "page",
  view: "view",
  unnamed: "unnamed",
  category: "category",
  capability: "capability",
  mission: "mission",
  condition: "condition",
  mode: "mode",
  level: "level",
  status: "status",
  maker: "maker",
  maker_country: "maker_country",
  operator: "operator",
  operator_country: "operator_country",
  programme: "programme",
  numeric: "numeric",
};

const UNNAMED_ON = "1";

const WHOLE_NUMBER = /^\d+$/;

// What parts a range's key from its bounds, and one bound from the other
const RANGE_SEPARATOR = ":";

const oneOf = <T extends string>(
  allowed: readonly T[],
  value: string | null,
  fallback: T,
): T => (allowed.includes(value as T) ? (value as T) : fallback);

const decodePage = (value: string | null): number => {
  if (!value || !WHOLE_NUMBER.test(value)) {
    return DEFAULT_EXPLORE_STATE.page;
  }
  const page = Number(value);
  return Number.isSafeInteger(page) && page >= 1
    ? page
    : DEFAULT_EXPLORE_STATE.page;
};

// The picks of one filter: no empty value, and none twice
const decodePicks = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)));

// A bound as text: empty for none
const encodeBound = (bound: number | null): string =>
  bound === null ? "" : String(bound);

// A bound from text: null for none, undefined for text that is no number
const decodeBound = (text: string): number | null | undefined => {
  if (text === "") {
    return null;
  }
  const bound = text.trim() === "" ? NaN : Number(text);
  return Number.isFinite(bound) ? bound : undefined;
};

// One range as `key:minimum:maximum`, the form the address and the search
// request carry
export const encodeExploreRange = (range: ExploreRange): string =>
  [range.key, encodeBound(range.minimum), encodeBound(range.maximum)].join(
    RANGE_SEPARATOR,
  );

// Reads one range from `key:minimum:maximum`; null for text that is none.
// The key may hold a colon itself.
export const decodeExploreRange = (text: string): ExploreRange | null => {
  const beforeMaximum = text.lastIndexOf(RANGE_SEPARATOR);
  const beforeMinimum = text.lastIndexOf(RANGE_SEPARATOR, beforeMaximum - 1);
  if (beforeMaximum < 0 || beforeMinimum < 0) {
    return null;
  }
  const key = text.slice(0, beforeMinimum);
  const minimum = decodeBound(text.slice(beforeMinimum + 1, beforeMaximum));
  const maximum = decodeBound(text.slice(beforeMaximum + 1));
  if (!key || minimum === undefined || maximum === undefined) {
    return null;
  }
  return { key, minimum, maximum };
};

// The ranges of an address: the first of each key, the unreadable left out
const decodeRanges = (values: string[]): ExploreRange[] => {
  const ranges: ExploreRange[] = [];
  for (const value of values) {
    const range = decodeExploreRange(value);
    if (range && !ranges.some((other) => other.key === range.key)) {
      ranges.push(range);
    }
  }
  return ranges;
};

// Reads the state from an address. A missing or unknown value is its default.
export const decodeExploreState = (params: URLSearchParams): ExploreState => {
  const picks = {} as ExplorePicks;
  for (const name of EXPLORE_FILTER_NAMES) {
    picks[name] = decodePicks(params.getAll(EXPLORE_PARAMS[name]));
  }
  return {
    keyword: (params.get(EXPLORE_PARAMS.keyword) ?? "").trim(),
    sort: oneOf(
      EXPLORE_SORTS,
      params.get(EXPLORE_PARAMS.sort),
      DEFAULT_EXPLORE_STATE.sort,
    ),
    page: decodePage(params.get(EXPLORE_PARAMS.page)),
    view: oneOf(
      EXPLORE_VIEW_MODES,
      params.get(EXPLORE_PARAMS.view),
      DEFAULT_EXPLORE_STATE.view,
    ),
    unnamed: params.get(EXPLORE_PARAMS.unnamed) === UNNAMED_ON,
    ...picks,
    numeric: decodeRanges(params.getAll(EXPLORE_PARAMS.numeric)),
  };
};

// Writes the state into an address. The parameters of `base` that are not
// Explore's own are kept; `base` itself is not changed.
export const encodeExploreState = (
  state: ExploreState,
  base?: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams(base);
  for (const name of Object.values(EXPLORE_PARAMS)) {
    params.delete(name);
  }

  const keyword = state.keyword.trim();
  if (keyword) {
    params.set(EXPLORE_PARAMS.keyword, keyword);
  }
  if (state.sort !== DEFAULT_EXPLORE_STATE.sort) {
    params.set(EXPLORE_PARAMS.sort, state.sort);
  }
  if (state.page !== DEFAULT_EXPLORE_STATE.page) {
    params.set(EXPLORE_PARAMS.page, String(state.page));
  }
  if (state.view !== DEFAULT_EXPLORE_STATE.view) {
    params.set(EXPLORE_PARAMS.view, state.view);
  }
  if (state.unnamed) {
    params.set(EXPLORE_PARAMS.unnamed, UNNAMED_ON);
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    for (const value of state[name]) {
      params.append(EXPLORE_PARAMS[name], value);
    }
  }
  for (const range of state.numeric) {
    params.append(EXPLORE_PARAMS.numeric, encodeExploreRange(range));
  }
  return params;
};

// The parts whose change leaves the list as it is, and so keeps the page
const KEEPS_PAGE: ReadonlySet<keyof ExploreState> = new Set<
  keyof ExploreState
>(["page", "view"]);

// Whether two values of one part are the same: picks and ranges by what
// they hold, the rest by identity
const samePart = (one: unknown, other: unknown): boolean =>
  one === other ||
  (Array.isArray(one) &&
    Array.isArray(other) &&
    JSON.stringify(one) === JSON.stringify(other));

// The state after a change. A change of anything that alters the list
// (keyword, sort, the unnamed switch, a pick, a range) returns to the first
// page.
export const changeExploreState = (
  state: ExploreState,
  change: Partial<ExploreState>,
): ExploreState => {
  const altersList = (Object.keys(change) as (keyof ExploreState)[]).some(
    (part) => !KEEPS_PAGE.has(part) && !samePart(change[part], state[part]),
  );
  return {
    ...state,
    ...change,
    ...(altersList ? { page: DEFAULT_EXPLORE_STATE.page } : {}),
  };
};

// The picks of one filter with a value picked or no longer picked
export const withExplorePick = (
  picks: readonly string[],
  value: string,
  picked: boolean,
): string[] => {
  if (!picked) {
    return picks.filter((pick) => pick !== value);
  }
  return picks.includes(value) ? [...picks] : [...picks, value];
};

// The ranges with the range of one key set. A range without a bound takes
// the key's range away: that is what emptying both fields of the panel means.
export const withExploreRange = (
  ranges: readonly ExploreRange[],
  range: ExploreRange,
): ExploreRange[] => {
  if (range.minimum === null && range.maximum === null) {
    return ranges.filter((other) => other.key !== range.key);
  }
  return ranges.some((other) => other.key === range.key)
    ? ranges.map((other) => (other.key === range.key ? range : other))
    : [...ranges, range];
};

// The number of active filters: every pick and every range
export const countExploreFilters = (state: ExploreFilterState): number =>
  EXPLORE_FILTER_NAMES.reduce(
    (count, name) => count + state[name].length,
    state.numeric.length,
  );

// What the filter panel asks for beside the state. None of it is part of
// the address.
export interface ExplorePanelRequest {
  // Most values of one flat filter; absent for the backend's default
  filterLimit?: number;
  // The text typed into one flat filter's box, which shortens that filter
  narrow?: { filter: ProductFlatFilterName; text: string } | null;
}

// The products a state selects, as the search and the suggestions take
// them: the keyword, the picks and ranges as the address holds them, and
// the unnamed switch
export const toProductSelectionParams = (
  state: ExploreState,
): ProductSelectionParams => {
  const picks: ProductFilterPicks = {};
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      picks[name] = state[name];
    }
  }
  return {
    ...(state.keyword ? { keyword: state.keyword } : {}),
    ...picks,
    ...(state.numeric.length > 0
      ? { numeric: state.numeric.map(encodeExploreRange) }
      : {}),
    ...(state.unnamed ? { include_unnamed: true } : {}),
  };
};

// The request of the product search for a state: the page of results and
// the whole filter panel, in one. Picks and ranges go into it as the
// address holds them.
export const toProductSearchParams = (
  state: ExploreState,
  panel: ExplorePanelRequest = {},
): ProductSearchParams => {
  const narrowText = panel.narrow?.text.trim();
  return {
    ...toProductSelectionParams(state),
    sort: state.sort,
    limit: EXPLORE_PAGE_SIZE,
    offset: (state.page - 1) * EXPLORE_PAGE_SIZE,
    include_filters: true,
    ...(panel.filterLimit ? { filter_limit: panel.filterLimit } : {}),
    ...(panel.narrow && narrowText
      ? { narrow_filter: panel.narrow.filter, narrow_text: narrowText }
      : {}),
  };
};
